import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  loadSettings,
  saveSettings,
  type AppSettingsV1,
  type Language,
  type Theme,
} from '../platform/settings/settings';
import { DARK_THEME_QUERY, resolveSystemTheme } from '../platform/theme/system-theme';

type AppSettingsController = {
  language: Language;
  resolvedTheme: Theme;
  setLanguage: (language: Language) => void;
  setTheme: (theme: Theme) => void;
};

function getThemeMediaQuery(): MediaQueryList {
  return window.matchMedia(DARK_THEME_QUERY);
}

export function useAppSettings(): AppSettingsController {
  const [settings, setSettings] = useState<AppSettingsV1>(() => loadSettings(window.localStorage));
  const [systemTheme, setSystemTheme] = useState<Theme>(() =>
    resolveSystemTheme(getThemeMediaQuery()),
  );

  useEffect(() => {
    const mediaQuery = getThemeMediaQuery();
    const updateSystemTheme = (): void => {
      setSystemTheme(resolveSystemTheme(mediaQuery));
    };

    mediaQuery.addEventListener('change', updateSystemTheme);
    return (): void => {
      mediaQuery.removeEventListener('change', updateSystemTheme);
    };
  }, []);

  const updateSettings = useCallback((nextSettings: AppSettingsV1): void => {
    setSettings(nextSettings);
    saveSettings(window.localStorage, nextSettings);
  }, []);

  const setLanguage = useCallback(
    (language: Language): void => {
      const nextSettings: AppSettingsV1 =
        settings.themeOverride === undefined
          ? { version: 1, language }
          : { version: 1, language, themeOverride: settings.themeOverride };
      updateSettings(nextSettings);
    },
    [settings.themeOverride, updateSettings],
  );

  const setTheme = useCallback(
    (themeOverride: Theme): void => {
      updateSettings({
        version: 1,
        language: settings.language,
        themeOverride,
      });
    },
    [settings.language, updateSettings],
  );

  const resolvedTheme = settings.themeOverride ?? systemTheme;

  useEffect(() => {
    document.documentElement.dataset.theme = resolvedTheme;
    document.documentElement.lang = settings.language;
  }, [resolvedTheme, settings.language]);

  return useMemo(
    () => ({
      language: settings.language,
      resolvedTheme,
      setLanguage,
      setTheme,
    }),
    [resolvedTheme, setLanguage, setTheme, settings.language],
  );
}
